import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";

/**
 * useHotSignalRealtime — Detection temps réel des contacts qui basculent en
 * heat_score 'hot'. App ouverte = vibration + toast immédiat avec lien vers la
 * fiche. App fermée = pas concerné (le push event-followup-cron ou un futur
 * cron hot-signal s'en charge — pas ce hook).
 *
 * Branche un seul channel Postgres Realtime sur immo_contacts (UPDATE) filtré
 * par team_id côté client, et déclenche un toast si new.heat_score === 'hot' ET
 * old.heat_score !== 'hot'.
 *
 * Pré-requis DB (migration 20260526150000_immo_contacts_realtime.sql) :
 *   - immo_contacts dans la publication supabase_realtime
 *   - REPLICA IDENTITY FULL (pour avoir payload.old.heat_score)
 *
 * À monter une seule fois dans le layout authentifié.
 */
export function useHotSignalRealtime(): void {
  const { user } = useAuth();
  const { toast } = useToast();
  // toast change de réf à chaque render. Le stocker en ref évite de mettre le
  // useEffect en deps → sinon le channel est sans cesse fermé/recréé avant
  // d'avoir le temps de SUBSCRIBE (status reste CLOSED, jamais d'events reçus).
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;

    let teamId: string | null = null;

    void (async () => {
      const { data } = await supabase
        .from("users")
        .select("team_id")
        .eq("id", userId)
        .maybeSingle();
      teamId = data?.team_id ?? null;
    })();

    const channel = supabase
      .channel("hot-signal-realtime")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "immo_contacts",
        },
        (payload) => {
          const newRow = payload.new as {
            id?: string;
            team_id?: string;
            heat_score?: string;
            first_name?: string | null;
            last_name?: string | null;
            name?: string | null;
          };
          const oldRow = payload.old as { heat_score?: string };

          if (!teamId || newRow.team_id !== teamId) return;
          if (newRow.heat_score !== "hot") return;
          if (oldRow.heat_score === "hot") return;

          const displayName =
            [newRow.first_name, newRow.last_name].filter(Boolean).join(" ").trim() ||
            newRow.name ||
            "Un contact";

          // Vibration si supportée (Android Chrome principalement)
          if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            try {
              navigator.vibrate([200, 100, 200]);
            } catch {
              // ignore — vibration optionnelle
            }
          }

          toastRef.current({
            title: "Signal chaud",
            description: `${displayName} vient de passer en hot. Ouvre la fiche dans le CRM.`,
            duration: 8000,
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId]);
}
